import { useMemo, useCallback } from 'react';

/**
 * @typedef {Object} Section
 * @property {number} id - Unique section identifier
 * @property {string} page_name - Page the section belongs to
 * @property {string} section_type - Type of section (e.g., 'hero_carousel', 'live_draw')
 * @property {number} section_order - Display order (lower numbers first)
 * @property {boolean} is_visible - Whether the section is visible
 * @property {Object} config - Section-specific configuration
 */

/**
 * @typedef {Object} SectionVisibilityReturn
 * @property {(sectionType: string) => boolean} isSectionVisible - Check if a section type is visible
 * @property {() => Section[]} getVisibleSections - Get all visible sections
 * @property {() => Section[]} getSortedSections - Get all sections sorted by display_order
 */

/**
 * Hook for managing section visibility based on page sections configuration.
 * Provides utilities to check visibility, filter visible sections, and sort by display order.
 *
 * @param {Section[]} sections - Array of section objects from usePageSections hook
 * @returns {SectionVisibilityReturn} Object containing visibility utilities
 *
 * @example
 * const { sections } = usePageSections('classic5050_home');
 * const { isSectionVisible, getVisibleSections, getSortedSections } = useSectionVisibility(sections);
 *
 * // Check if a specific section should be shown
 * if (isSectionVisible('live_draw')) {
 *   // render live draw component
 * }
 *
 * // Get only visible sections for rendering
 * const visibleSections = getVisibleSections();
 *
 * // Get all sections sorted by display order
 * const sortedSections = getSortedSections();
 */
export function useSectionVisibility(sections) {
  /**
   * Check if a section type should be visible.
   * Returns false if the section is not found (defaults to hidden).
   *
   * @param {string} sectionType - The section_type to check
   * @returns {boolean} Whether the section should be displayed
   */
  const isSectionVisible = useCallback(
    (sectionType) => {
      const section = sections.find((s) => s.section_type === sectionType);
      return section ? section.is_visible : false;
    },
    [sections]
  );

  /**
   * Get all sections that are marked as visible.
   *
   * @returns {Section[]} Array of visible sections
   */
  const getVisibleSections = useCallback(
    () => sections.filter((s) => s.is_visible),
    [sections]
  );

  /**
   * Get all sections sorted by display_order (ascending).
   * Sections with lower section_order values appear first.
   *
   * @returns {Section[]} Array of sections sorted by display_order
   */
  const getSortedSections = useCallback(
    () => [...sections].sort((a, b) => a.section_order - b.section_order),
    [sections]
  );

  return {
    isSectionVisible,
    getVisibleSections,
    getSortedSections,
  };
}

export default useSectionVisibility;
